import type { PlanPartyType } from '../../types';

/**
 * "Who's responsible" party types (Glenn's Sept 2026 ask, revised Sept 11
 * after his call: general jurisdiction levels rather than a long specific
 * list). Key + label live here, in one plain array, so Glenn's edits later
 * are a one-line change each. The keys themselves are the validated enum in
 * src/schemas/firestoreSchemas.ts (planPartyTypeSchema) — this file is
 * display only and must not drift from that list.
 *
 * `other` is an escape hatch Glenn didn't ask for — flagged to him rather
 * than silently added; see the delivery report.
 */
export interface PlanPartyTypeDef {
  key: PlanPartyType;
  label: string;
}

export const PLAN_PARTY_TYPES: PlanPartyTypeDef[] = [
  { key: 'homeowner', label: 'Homeowner' },
  { key: 'hoa', label: 'Homeowner association' },
  { key: 'unincorporated', label: 'Unincorporated community' },
  { key: 'city', label: 'City' },
  { key: 'county', label: 'County' },
  { key: 'state', label: 'State' },
  { key: 'national', label: 'National' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'other', label: 'Other' },
];

export const PLAN_PARTY_TYPE_META: Record<PlanPartyType, PlanPartyTypeDef> = Object.fromEntries(
  PLAN_PARTY_TYPES.map((type) => [type.key, type])
) as Record<PlanPartyType, PlanPartyTypeDef>;

// A party's display name: organization and person together when both are
// set ("Big Bear Fire Safe Council, Jane Doe"), whichever one is set alone
// otherwise. Every party has at least one per PlanPartySchema's refine.
export function partyDisplayName(party: { organization?: string; person?: string }): string {
  return [party.organization, party.person].filter(Boolean).join(', ');
}

/** The one name for a comma-joined "who" line: the person, else the
 *  organization. The full "Org, Person" form reads as two parties there. */
export function partyShortName(party: { organization?: string; person?: string }): string {
  return party.person || party.organization || '';
}
